import React, { useState, useEffect } from 'react';
import './POSOpenSalesModal.css';
import apiService from '../../services/api';
import invoiceGenerator from '../../services/invoiceGenerator';

const POSOpenSalesModal = ({ onClose, defaultDate, onEdit, onDelete }) => {
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Filters
  const [date, setDate] = useState(defaultDate || new Date().toISOString().split('T')[0]);
  const [customer, setCustomer] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  
  useEffect(() => {
    loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSales = async () => {
    setIsLoading(true);
    try {
      const params = { 
        per_page: -1, // Get all recently to allow broad local filtering without API timezone issues
      };
      
      const res = await apiService.getSales(params);
      const data = res.data || res || [];
      
      // Transform safely
      const transformed = data.map(sale => ({
        id: sale.id,
        invoiceNo: sale.invoice_no,
        customer: sale.contact?.name || sale.customer || 'Walk-in Customer',
        dateTime: sale.transaction_date || sale.created_at,
        subTotal: parseFloat(sale.total_before_tax || sale.final_total || 0),
        tax: parseFloat(sale.tax_amount || 0),
        discount: parseFloat(sale.discount_amount || 0),
        total: parseFloat(sale.final_total || 0),
        paidAmount: parseFloat(sale.total_paid !== undefined ? sale.total_paid : (sale.payment_lines ? sale.payment_lines.reduce((s,p) => s + parseFloat(p.amount||0),0) : 0)),
        items: sale.sell_lines || sale.products || [],
        apiData: sale
      }));
      
      setSales(transformed);
      filterSales(transformed, date, customer, invoiceNo);
    } catch (error) {
      console.error('Failed to load sales:', error);
    }
    setIsLoading(false);
  };
  
  const filterSales = (list, d, c, inv) => {
    let result = list;
    // Since we filtered via API for the date, we can be more lenient or rely on API
    if (d) {
      // Loose match on date string, primarily rely on API filtering
      result = result.filter(s => s.dateTime && s.dateTime.includes(d.substring(0, 10)));
    }
    if (c) {
      result = result.filter(s => s.customer.toLowerCase().includes(c.toLowerCase()));
    }
    if (inv) {
      result = result.filter(s => s.invoiceNo && s.invoiceNo.toLowerCase().includes(inv.toLowerCase()));
    }
    setFilteredSales(result);
    // Select first by default if exists
    if (result.length > 0) {
      setSelectedSale(result[0]);
    } else {
      setSelectedSale(null);
    }
  };
  
  const handleSearch = () => {
    filterSales(sales, date, customer, invoiceNo);
  };
  
  const handlePrint = (sale) => {
    if (!sale) return;
    const invoiceUrl = sale.apiData?.invoice_url || sale.invoiceUrl;
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank');
    } else {
      alert('Invoice URL not available for this sale. It may be a Draft or generated without a URL.');
    }
  };

  const handleDelete = async (sale) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${sale.invoiceNo}?`)) return;
    try {
      await apiService.deleteSale(sale.id);
      if (onDelete) onDelete(sale.id);
      loadSales(); // Refresh list
    } catch (error) {
      alert(`Failed to delete sale: ${error.message}`);
    }
  };

  return (
    <div className="pos-open-sales-modal" onClick={onClose}>
      <div className="pos-osm-content" onClick={e => e.stopPropagation()}>
        <div className="pos-osm-header">
          <h2>Sales</h2>
          <button className="pos-osm-close" onClick={onClose}>×</button>
        </div>
        
        <div className="pos-osm-filters">
          <input type="date" className="pos-osm-input" value={date} onChange={e => { setDate(e.target.value); filterSales(sales, e.target.value, customer, invoiceNo); }} />
          <select className="pos-osm-input" value={customer} onChange={e => { setCustomer(e.target.value); filterSales(sales, date, e.target.value, invoiceNo); }}>
            <option value="">Customer</option>
            {[...new Set(sales.map(s => s.customer))].filter(Boolean).map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select className="pos-osm-input">
            <option>All Sales</option>
            <option>Completed</option>
            <option>Pending</option>
          </select>
          <input type="text" className="pos-osm-input" placeholder="Invoice No" value={invoiceNo} onChange={e => { setInvoiceNo(e.target.value); filterSales(sales, date, customer, e.target.value); }} />
          <div style={{ flexBasis: '100%', display: 'flex' }}>
            <button className="pos-osm-search-btn" onClick={handleSearch}>Search</button>
          </div>
        </div>
        
        {isLoading ? (
          <div className="pos-osm-loading">Loading sales...</div>
        ) : (
          <div className="pos-osm-body">
            {/* LEFT TAB */}
            <div className="pos-osm-left">
              <table className="pos-osm-table">
                <thead>
                  <tr>
                    <th>Invoice No</th>
                    <th>Customer</th>
                    <th>Date Time</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.map(sale => (
                    <tr 
                      key={sale.id} 
                      className={selectedSale?.id === sale.id ? 'active' : ''}
                      onClick={() => setSelectedSale(sale)}
                    >
                      <td>{sale.invoiceNo}</td>
                      <td>{sale.customer}</td>
                      <td>{new Date(sale.dateTime).toLocaleString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit' }).replace(',', '')}</td>
                    </tr>
                  ))}
                  {filteredSales.length === 0 && (
                     <tr><td colSpan="3" style={{textAlign: 'center', padding: '20px'}}>No sales found for this date.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* RIGHT TAB */}
            <div className="pos-osm-right">
              <div className="pos-osm-detail-title">Sale Details</div>
              
              <div className="pos-osm-detail-header">
                <div>Invoice No: {selectedSale ? selectedSale.invoiceNo : '—'}</div>
                <div style={{ textAlign: 'right' }}>Date Time: {selectedSale ? new Date(selectedSale.dateTime).toLocaleString('en-GB') : '—'}</div>
                <div>Customer: {selectedSale ? selectedSale.customer : '—'}</div>
              </div>
              
              <div className="pos-osm-detail-items">
                <table className="pos-osm-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Price</th>
                      <th>Qty</th>
                      <th>Discount</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSale ? selectedSale.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.product?.name || item.name || item.product?.product_name || `Product ID: ${item.product_id}`}</td>
                        <td>{(parseFloat(item.unit_price || item.unit_price_inc_tax || 0)).toFixed(2)}</td>
                        <td>{item.quantity}</td>
                        <td>{(parseFloat(item.line_discount_amount || 0)).toFixed(2)}</td>
                        <td>{((parseFloat(item.unit_price || item.unit_price_inc_tax || 0) * item.quantity) - parseFloat(item.line_discount_amount || 0)).toFixed(2)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                          Select a sale to view items
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="pos-osm-summary">
                <div>Sub Total: {selectedSale ? selectedSale.subTotal.toFixed(2) : '0.00'}</div>
                <div>Total Item: {selectedSale ? `${selectedSale.items.reduce((s, i) => s + (i.quantity || 1), 0)} (${selectedSale.items.length})` : '0 (0)'}</div>
                <div>Tax: {selectedSale ? selectedSale.tax.toFixed(2) : '0.00'}</div>
                <div>Charge: 0.00</div>
                <div>Discount: {selectedSale ? selectedSale.discount.toFixed(2) : '0.00(0.00)'}</div>
                <div></div>
              </div>
              
              <div className="pos-osm-payable">
                Total Payable: {selectedSale ? selectedSale.total.toFixed(2) : '0.00'}
              </div>
              
              <div className="pos-osm-payments">
                <div>Paid Amount: {selectedSale ? selectedSale.paidAmount.toFixed(2) : '0.00'}</div>
                <div>Due Amount: {selectedSale ? Math.max(0, selectedSale.total - selectedSale.paidAmount).toFixed(2) : '0.00'}</div>
              </div>
              
              <div className="pos-osm-footer-btns">
                <button 
                  className="pos-osm-btn pos-osm-btn--print" 
                  onClick={() => selectedSale && handlePrint(selectedSale)}
                >
                  Print Invoice
                </button>
                <button 
                  className="pos-osm-btn pos-osm-btn--edit"
                  onClick={() => selectedSale && onEdit && onEdit(selectedSale)}
                >
                  Edit
                </button>
                <button 
                  className="pos-osm-btn pos-osm-btn--delete"
                  onClick={() => selectedSale && handleDelete(selectedSale)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default POSOpenSalesModal;
